import { useEffect, useRef, useState } from "react";
import { Loader2, ShieldCheck, AlertCircle, Lock } from "lucide-react";
import {
  crearCaptureContext,
  extraerFlexLib,
} from "@/data/cybersourceStore";

// Simulación E2E (nunca en producción real). Local para no acoplar el mock
// de pruebas de F1A a otros módulos (axios).
const SIMULACION_3DS =
  String(process.env.REACT_APP_CYBERSOURCE_SIMULATE || "").toLowerCase() === "true";

/**
 * Campos alojados de Flex Microform v2 (modo BanBajío) · Sprint 59 · F1A.
 *
 * Renderiza SOLO contenedores (`<div>`) para número de tarjeta y CVV; los
 * inputs reales viven dentro de iframes de CyberSource. CT Connect nunca
 * recibe PAN/CVV. Genera el token transitorio y lo entrega enmascarado a
 * `onToken`; el token completo permanece en memoria (nunca en URL/storage).
 *
 * Esta fase NO autoriza ni captura el pago ni emite boletos.
 */

const CARGA_SRC_TIMEOUT_MS = 15000;

// Hosts oficiales de la biblioteca cliente de Flex Microform v2 (TEST y PROD).
const FLEX_HOSTS_PERMITIDOS = new Set([
  "flex.cybersource.com",
  "testflex.cybersource.com",
]);

// Valida que clientLibrary sea HTTPS y pertenezca a un host oficial de Flex.
function validarClientLibrary(src) {
  let url;
  try {
    url = new URL(String(src || ""));
  } catch {
    throw new Error("La biblioteca de pago seguro tiene una URL inválida.");
  }
  if (url.protocol !== "https:") {
    throw new Error("La biblioteca de pago seguro debe servirse por HTTPS.");
  }
  if (!FLEX_HOSTS_PERMITIDOS.has(url.hostname)) {
    throw new Error("La biblioteca de pago seguro no proviene de un host autorizado.");
  }
  return url.href;
}

// Inserta la biblioteca Flex EXACTAMENTE una vez. Los manejadores onload/onerror
// se asignan ANTES de insertar el <script>; integrity/crossOrigin se aplican
// cuando el contrato los provee; hay timeout explícito (nunca spinner infinito)
// y, ante error o timeout, se retira el <script> fallido y se rechaza para que
// la UI ofrezca reintento manual. Si window.Flex ya existe, termina de inmediato.
function cargarFlexLib(src, integrity) {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.Flex) {
      resolve();
      return;
    }
    const href = validarClientLibrary(src);
    const existente = document.querySelector('script[data-cs-flex="1"]');
    if (existente) {
      if (existente.getAttribute("data-cs-loaded") === "1") {
        resolve();
        return;
      }
      existente.addEventListener("load", () => resolve(), { once: true });
      existente.addEventListener(
        "error",
        () => reject(new Error("No se pudo cargar el módulo de pago seguro.")),
        { once: true },
      );
      return;
    }
    const s = document.createElement("script");
    let finalizado = false;
    let timer = null;
    const fallar = (msg) => {
      if (finalizado) return;
      finalizado = true;
      if (timer) clearTimeout(timer);
      if (s.parentNode) s.parentNode.removeChild(s); // retira el <script> fallido
      reject(new Error(msg));
    };
    const ok = () => {
      if (finalizado) return;
      finalizado = true;
      if (timer) clearTimeout(timer);
      s.setAttribute("data-cs-loaded", "1");
      resolve();
    };
    // Manejadores asignados ANTES de insertar el <script>.
    s.onload = ok;
    s.onerror = () => fallar("No se pudo cargar el módulo de pago seguro.");
    timer = setTimeout(
      () => fallar("Se agotó el tiempo de carga del módulo de pago seguro."),
      CARGA_SRC_TIMEOUT_MS,
    );
    s.src = href;
    if (integrity) {
      s.integrity = integrity; // SRI del contrato
    }
    s.crossOrigin = "anonymous";
    s.async = true;
    s.setAttribute("data-cs-flex", "1");
    document.head.appendChild(s);
  });
}

const maskToken = (tok) => {
  const s = String(tok || "");
  if (s.length <= 8) return "••••";
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
};

// Token transitorio FICTICIO para simulación E2E (jti codifica el escenario).
// No es un JWT real ni contiene datos de tarjeta.
const _b64url = (obj) =>
  btoa(JSON.stringify(obj)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const _fakeTransient = (jti) =>
  `${_b64url({ alg: "none", typ: "JWT" })}.${_b64url({ jti, ficticio: true })}.sim`;

export const MicroformFields = ({ compraId, onToken, onError, disabled = false }) => {
  const [fase, setFase] = useState("inicial"); // inicial|cargando|listo|tokenizando|token|error|expirado
  const [mensajeError, setMensajeError] = useState("");
  const [tokenMask, setTokenMask] = useState("");
  // Expiración: datos NO sensibles capturados fuera de los iframes de Microform
  // (PAN y CVV siguen SOLO dentro de los iframes del banco). Se envían a
  // createToken para que queden dentro del token transitorio.
  const [expMes, setExpMes] = useState("");
  const [expAnio, setExpAnio] = useState("");
  const microformRef = useRef(null);
  const tokenizandoRef = useRef(false);
  const tokenRef = useRef(null); // token transitorio · SOLO memoria.
  const montadoRef = useRef(true);
  // Inicialización COMPARTIDA por compraId: capture-context + carga de la
  // biblioteca Flex ocurren UNA sola vez y se reutilizan entre el doble montaje
  // de React StrictMode. NO se cancela en el cleanup del efecto (eso dejaba el
  // spinner infinito): sólo el montaje vivo instancia el Microform sobre el DOM
  // presente. Un compraId nuevo o un reintento manual reinician la init.
  const initRef = useRef({ compraId: null, promise: null });
  const [retryTick, setRetryTick] = useState(0);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!compraId) {
      setFase("inicial");
      return undefined;
    }
    // Simulación E2E: no se carga Flex real ni se llama a capture-context.
    if (SIMULACION_3DS) {
      setFase("sim");
      return undefined;
    }
    let activo = true;
    setFase("cargando");
    setMensajeError("");
    // capture-context + biblioteca Flex: UNA sola vez por compraId, compartido
    // entre montajes de StrictMode (no se recrea si ya está en curso/listo). No
    // se repite capture-context automáticamente; sólo un reintento manual o un
    // compraId distinto lo reinician.
    if (initRef.current.compraId !== compraId) {
      initRef.current = {
        compraId,
        promise: (async () => {
          const ctx = await crearCaptureContext(compraId);
          if (!ctx || ctx.error || !ctx.captureContext) {
            throw new Error(
              typeof ctx?.error === "string"
                ? ctx.error
                : "No se pudo iniciar el pago seguro.",
            );
          }
          const { clientLibrary, clientLibraryIntegrity } = extraerFlexLib(
            ctx.captureContext,
          );
          await cargarFlexLib(clientLibrary, clientLibraryIntegrity);
          return ctx;
        })(),
      };
    }
    const promesaInit = initRef.current.promise;
    (async () => {
      try {
        const ctx = await promesaInit;
        // Sólo el montaje vivo instancia el Microform sobre el DOM presente.
        if (!activo || !montadoRef.current) return;
        // eslint-disable-next-line no-undef
        const flex = new window.Flex(ctx.captureContext);
        const microform = flex.microform({ styles: {} });
        microform.createField("number", { placeholder: "•••• •••• •••• ••••" }).load("#cs-card-number");
        microform.createField("securityCode", { placeholder: "•••", maxLength: 4 }).load("#cs-card-cvv");
        microformRef.current = microform;
        setFase("listo");
      } catch (e) {
        if (!activo) return;
        // Libera la init para permitir un reintento manual (nunca automático).
        initRef.current = { compraId: null, promise: null };
        const msg = e?.message || "No se pudo iniciar el pago seguro.";
        setMensajeError(msg);
        setFase("error");
        onErrorRef.current?.(msg);
      }
    })();
    return () => {
      activo = false;
    };
  }, [compraId, retryTick]);

  // Reintento MANUAL (nunca automático) tras un error o timeout de carga.
  const reintentarCarga = () => {
    initRef.current = { compraId: null, promise: null };
    microformRef.current = null;
    setMensajeError("");
    setFase("cargando");
    setRetryTick((n) => n + 1);
  };

  const tokenizar = () => {
    if (disabled || tokenizandoRef.current || fase === "token") return; // anti doble clic
    if (!microformRef.current) return;
    const mm = expMes.trim();
    const yyyy = expAnio.trim();
    if (!/^(0[1-9]|1[0-2])$/.test(mm) || !/^\d{4}$/.test(yyyy)) {
      setMensajeError("Ingresa el mes (MM) y el año (AAAA) de expiración.");
      return;
    }
    tokenizandoRef.current = true;
    setFase("tokenizando");
    setMensajeError("");
    microformRef.current.createToken(
      { expirationMonth: expMes.trim(), expirationYear: expAnio.trim() },
      (err, transientToken) => {
      tokenizandoRef.current = false;
      if (err || !transientToken) {
        const msg = err?.message || "Revisa los datos de tu tarjeta.";
        setMensajeError(msg);
        setFase("listo");
        onError?.(msg);
        return;
      }
      tokenRef.current = transientToken; // memoria; nunca a storage/URL/logs
      setTokenMask(maskToken(transientToken));
      setFase("token");
      // Se propaga el JWT transitorio COMPLETO (memoria del padre). NUNCA se
      // guarda en URL/storage/logs; el contrato 3DS exige el JWT íntegro.
      onToken?.(maskToken(transientToken), transientToken);
    });
  };

  const tokenizarSim = (escenario) => {
    if (fase === "token") return;
    const jti = `SIM-${escenario}-${Math.random().toString(36).slice(2, 10)}`;
    const tok = _fakeTransient(jti);
    tokenRef.current = tok;
    setTokenMask(maskToken(tok));
    setFase("token");
    onToken?.(maskToken(tok), tok);
  };

  return (
    <section
      data-testid="microform-container"
      className="rounded-2xl border hairline bg-card p-6 md:p-8 shadow-soft"
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="w-9 h-9 grid place-items-center rounded-xl bg-accent/10 text-accent">
          <Lock className="w-4 h-4" strokeWidth={1.7} />
        </span>
        <div className="flex flex-col">
          <h2 className="font-heading text-lg text-foreground">Pago seguro con tarjeta</h2>
          <span className="text-xs text-muted-foreground">
            Los datos se capturan cifrados por el banco. CT Connect no ve tu tarjeta.
          </span>
        </div>
      </div>

      {fase === "inicial" && (
        <p data-testid="microform-status" className="text-sm text-muted-foreground">
          Inicia la compra para continuar con el pago con tarjeta.
        </p>
      )}

      {fase === "cargando" && (
        <p data-testid="microform-status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando el módulo de pago seguro…
        </p>
      )}

      {fase === "sim" && (
        <div data-testid="microform-sim" className="flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">
            Modo simulación E2E (sin CyberSource real). Elige un escenario:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button type="button" data-testid="sim-frictionless-btn" onClick={() => tokenizarSim("FRICTIONLESS")} disabled={disabled}
              className="rounded-full px-4 py-3 text-sm font-semibold bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground shadow-soft">
              Frictionless
            </button>
            <button type="button" data-testid="sim-challenge-btn" onClick={() => tokenizarSim("CHALLENGE")} disabled={disabled}
              className="rounded-full px-4 py-3 text-sm font-semibold bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground shadow-soft">
              Challenge
            </button>
            <button type="button" data-testid="sim-reject-btn" onClick={() => tokenizarSim("REJECT")} disabled={disabled}
              className="rounded-full px-4 py-3 text-sm font-semibold bg-destructive text-white shadow-soft">
              Rechazo 3DS
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Escenarios de autorización/captura simulada (F2A):
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button type="button" data-testid="sim-pago-rechazo-btn" onClick={() => tokenizarSim("FRICTIONLESS-PAYDECL")} disabled={disabled}
              className="rounded-full px-4 py-3 text-sm font-semibold border hairline bg-background text-foreground shadow-soft">
              Pago rechazado
            </button>
            <button type="button" data-testid="sim-pago-desconocido-btn" onClick={() => tokenizarSim("FRICTIONLESS-PAYTIMEOUT")} disabled={disabled}
              className="rounded-full px-4 py-3 text-sm font-semibold border hairline bg-background text-foreground shadow-soft">
              Pago no confirmado
            </button>
          </div>
        </div>
      )}

      <div className={fase === "cargando" || fase === "inicial" || fase === "sim" ? "hidden" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        <label className="flex flex-col gap-2 sm:col-span-2">
          <span className="text-xs text-muted-foreground">Número de tarjeta</span>
          <div
            id="cs-card-number"
            data-testid="microform-number"
            className="flex items-center w-full h-[52px] px-4 rounded-xl bg-background border hairline overflow-hidden transition-colors [&>iframe]:h-full [&>iframe]:w-full [&>iframe]:block [&.flex-microform-focused]:ring-2 [&.flex-microform-focused]:ring-primary/40 [&.flex-microform-invalid]:border-red-500"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">CVV</span>
          <div
            id="cs-card-cvv"
            data-testid="microform-cvv"
            className="flex items-center w-full h-[52px] px-4 rounded-xl bg-background border hairline overflow-hidden transition-colors [&>iframe]:h-full [&>iframe]:w-full [&>iframe]:block [&.flex-microform-focused]:ring-2 [&.flex-microform-focused]:ring-primary/40 [&.flex-microform-invalid]:border-red-500"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-xs text-muted-foreground">Expiración</span>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={2}
              placeholder="MM"
              aria-label="Mes de expiración"
              data-testid="microform-exp-month"
              value={expMes}
              onChange={(e) => setExpMes(e.target.value.replace(/\D/g, "").slice(0, 2))}
              className="w-1/2 px-4 rounded-xl bg-background border hairline h-[52px]"
            />
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="AAAA"
              aria-label="Año de expiración"
              data-testid="microform-exp-year"
              value={expAnio}
              onChange={(e) => setExpAnio(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="w-1/2 px-4 rounded-xl bg-background border hairline h-[52px]"
            />
          </div>
        </label>
      </div>

      {mensajeError && (
        <p data-testid="microform-error" className="mt-4 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4" /> {mensajeError}
        </p>
      )}

      {fase === "error" && (
        <button
          type="button"
          data-testid="microform-retry-btn"
          onClick={reintentarCarga}
          disabled={disabled}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold border hairline bg-background text-foreground shadow-soft transition-all active:scale-[0.99] hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          Reintentar carga del módulo de pago
        </button>
      )}

      {fase === "token" ? (
        <p data-testid="microform-token-ok" className="mt-5 flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
          <ShieldCheck className="w-4 h-4" /> Token de pago generado ({tokenMask}). La autorización se completará en la siguiente fase.
        </p>
      ) : fase === "sim" ? null : (
        <button
          type="button"
          data-testid="microform-tokenizar-btn"
          onClick={tokenizar}
          disabled={disabled || fase !== "listo"}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-wide bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft transition-all active:scale-[0.99] hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {fase === "tokenizando" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Validando tarjeta…
            </>
          ) : (
            "Generar token de pago seguro"
          )}
        </button>
      )}
    </section>
  );
};

export default MicroformFields;
